import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QuillEditor from '../../../components/MasterAdmin/ui/QuillEditor';
import BlogSeoFields, {
  BlogSeoFormValues,
  blogSeoFromBlog,
} from '../../../components/Writer/Blogs/BlogSeoFields';
import { getCategories } from '../../../services/Writer/blogCategoriesService';
import { getBlogById, updateBlog } from '../../../services/Writer/blogService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { hydrateBlogEditorContent } from '../../../utils/quillContent';
import ReferenceLinksField, {
  validateAndNormalizeReferenceLinks,
} from '../../../components/Writer/ReferenceLinksField';
import { Calendar } from 'lucide-react';

type Category = { categoryId: string; name: string };

type Blog = {
  blogId: string;
  title: string;
  content: string;
  featuredImage?: string | null;
  status: 'draft' | 'published';
  adminApproval?: 'pending' | 'approved' | 'rejected' | null;
  rejectionReason?: string | null;
  referenceLinks?: string[] | null;
  blogDate?: string | null;
  authorId: string;
  authorType: 'doctor' | 'blog_writer' | 'writer' | 'admin';
  categoryId: string;
  createdAt: string;
  updatedAt: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  slug?: string | null;
  focusKeyword?: string | null;
  secondaryKeywords?: string | null;
  recommendedSchema?: string | null;
};

const toDateInput = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const EditBlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const userId = useMemo(() => localStorage.getItem('userId') || '', []);
  const blogDateInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState<Blog | null>((location.state as any) || null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [blogDate, setBlogDate] = useState('');
  const [seo, setSeo] = useState<BlogSeoFormValues>(blogSeoFromBlog(null));
  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [referenceLinks, setReferenceLinks] = useState<string[]>(['']);

  // Quill
  const [editorValue, setEditorValue] = useState<any>('');
  const [contentOpsJson, setContentOpsJson] = useState<string>('');

  useEffect(() => {
    if (!featuredImageFile) {
      setImagePreview(blog?.featuredImage || null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(featuredImageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [featuredImageFile, blog?.featuredImage]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const data = await getCategories();
        setCategories(data);
      } catch (e: any) {
        toast.error(getApiErrorMessage(e, 'Failed to load categories'));
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // Always fetch by id so SEO fields (and other columns not on list state) are present.
        const data = await getBlogById(id);
        setBlog(data);

        setTitle(data.title || '');
        setCategoryId(data.categoryId || '');
        setStatus(data.status || 'draft');
        setBlogDate(toDateInput(data.blogDate || data.createdAt));
        setSeo(blogSeoFromBlog(data));

        const links = Array.isArray(data.referenceLinks)
          ? data.referenceLinks.map((l: unknown) => String(l || '').trim()).filter(Boolean)
          : [];
        setReferenceLinks(links.length ? links : ['']);

        const { editorHtml, contentOpsJson } = hydrateBlogEditorContent(data.content);
        setEditorValue(editorHtml);
        setContentOpsJson(contentOpsJson);
      } catch (e: any) {
        toast.error(getApiErrorMessage(e, 'Failed to load blog'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    // Preserve doctor authorship; only require logged-in writer for writer-owned posts.
    const isDoctorBlog = blog?.authorType === 'doctor';
    const authorId = isDoctorBlog ? blog?.authorId : userId;
    const authorType = isDoctorBlog ? 'doctor' : blog?.authorType || 'writer';
    if (!authorId) {
      toast.error('Missing author. Please logout and login again.');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!categoryId) {
      toast.error('Category is required');
      return;
    }

    const contentToSend = contentOpsJson || String(editorValue || '').trim();
    if (!contentToSend) {
      toast.error('Content is required');
      return;
    }

    let normalizedLinks: string[] | null = null;
    try {
      normalizedLinks = validateAndNormalizeReferenceLinks(referenceLinks);
    } catch (err: any) {
      toast.error(err?.message || 'Invalid reference link');
      return;
    }

    setSubmitting(true);
    try {
      await updateBlog(id, {
        title: title.trim(),
        content: contentToSend,
        categoryId,
        status,
        blogDate: blogDate ? new Date(`${blogDate}T00:00:00`).toISOString() : undefined,
        authorId,
        authorType,
        featuredImageFile,
        referenceLinks: normalizedLinks,
        metaTitle: seo.metaTitle.trim() || null,
        metaDescription: seo.metaDescription.trim() || null,
        slug: seo.slug.trim() || null,
        focusKeyword: seo.focusKeyword.trim() || null,
        secondaryKeywords: seo.secondaryKeywords.trim() || null,
        recommendedSchema: seo.recommendedSchema.trim() || null,
      });
      toast.success('Blog updated');
      setTimeout(
        () => navigate(isDoctorBlog ? '/writer/doctor-blogs' : '/writer/blogs'),
        600
      );
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Failed to update blog'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-600 dark:text-gray-400">Loading...</div>;
  }

  if (!blog) {
    return <div className="py-16 text-center text-gray-600 dark:text-gray-400">Blog not found.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="flex items-center justify-between mb-4">
        <div className="text-2xl font-bold text-gray-900 dark:text-white">Edit Blog</div>
        <button
          onClick={() =>
            navigate(blog.authorType === 'doctor' ? '/writer/doctor-blogs' : '/writer/blogs')
          }
          className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
        >
          Back
        </button>
      </div>

      {blog.adminApproval === 'rejected' ? (
        <div className="mb-5 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          <div className="font-semibold">Rejected</div>
          <div className="mt-1">
            {blog.rejectionReason?.trim()
              ? `Reason: ${blog.rejectionReason.trim()}`
              : 'No rejection reason was provided.'}
          </div>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            placeholder="Enter title"
            maxLength={200}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category *</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={loadingCategories}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="">{loadingCategories ? 'Loading...' : 'Select category'}</option>
              {categories.map((c) => (
                <option key={c.categoryId} value={c.categoryId}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Blog Date</label>
          <div
            className="relative cursor-pointer"
            onClick={() => {
              const el = blogDateInputRef.current;
              if (!el) return;
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (el as any).showPicker?.();
              } catch {
                // ignore
              }
              el.focus();
            }}
          >
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-100 pointer-events-none" />
            <input
              ref={blogDateInputRef}
              type="date"
              value={blogDate}
              onChange={(e) => setBlogDate(e.target.value)}
              onClick={(e) => {
                const el = e.currentTarget;
                try {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (el as any).showPicker?.();
                } catch {
                  // ignore
                }
              }}
              className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content *</label>
          <QuillEditor
            value={editorValue}
            onChange={(html) => setEditorValue(html)}
            onChangeDelta={(ops) => setContentOpsJson(JSON.stringify(ops))}
            height="360px"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Featured Image</label>
          {imagePreview ? (
            <div className="mb-2">
              <img
                src={imagePreview}
                alt="Featured preview"
                className="max-h-56 rounded-lg border border-gray-200 dark:border-gray-700 object-contain"
              />
              {featuredImageFile ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  New image selected: {featuredImageFile.name}
                </p>
              ) : null}
            </div>
          ) : null}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              if (file && !file.type.startsWith('image/')) {
                toast.error('Please select a valid image file');
                e.target.value = '';
                setFeaturedImageFile(null);
                return;
              }
              setFeaturedImageFile(file);
            }}
            className="block w-full text-sm text-gray-500 dark:text-gray-400"
          />
        </div>

        <ReferenceLinksField value={referenceLinks} onChange={setReferenceLinks} />

        <BlogSeoFields value={seo} onChange={setSeo} />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/writer/blogs')}
            className="px-5 py-2.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {submitting ? 'Saving...' : 'Update'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBlogPage;
